import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getCourseColorOption } from '../lib/courseColors';

// Course color mapping
const courseColors = {
  'forklift': { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500' },
  'crane': { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-500' },
  'construction': { bg: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-500' },
  'safety': { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-700', border: 'border-green-500' },
  'height': { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-500' },
  'electric': { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-700', border: 'border-red-500' },
  'default': { bg: 'bg-gray-500', light: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-500' }
};

const getCourseColor = (courseId) => {
  if (courseId?.includes('forklift')) return courseColors.forklift;
  if (courseId?.includes('crane')) return courseColors.crane;
  if (courseId?.includes('construction') || courseId?.includes('vehicle')) return courseColors.construction;
  if (courseId?.includes('safety')) return courseColors.safety;
  if (courseId?.includes('height') || courseId?.includes('高所')) return courseColors.height;
  if (courseId?.includes('electric') || courseId?.includes('電気')) return courseColors.electric;
  return courseColors.default;
};

const ScheduleCalendar = ({ schedules = [], courses = [] }) => {
  const { t, language } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedDaySchedules, setSelectedDaySchedules] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  // 管理画面で講習に色が設定されていればそれを使用、無ければ従来の自動判定
  const getColorFor = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return getCourseColorOption(course?.color) || getCourseColor(courseId);
  };

  // Helper to get localized course name
  const getCourseName = (schedule) => {
    const course = courses.find(c => c.id === schedule.courseId);
    if (course?.name && typeof course.name === 'object') {
      return course.name[language] || course.name.ja || schedule.courseName;
    }
    return schedule.courseName;
  };

  const weekDaysArr = t('scheduleCalendar.weekDays');
  const weekDays = Array.isArray(weekDaysArr) ? weekDaysArr : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const monthsArr = t('scheduleCalendar.months');
  const monthNames = Array.isArray(monthsArr) ? monthsArr : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentDate);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedSchedule(null);
    setSelectedDaySchedules([]);
    setSelectedDay(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedSchedule(null);
    setSelectedDaySchedules([]);
    setSelectedDay(null);
  };

  const handleDayClick = (day) => {
    const daySchedules = getSchedulesForDate(day);
    if (daySchedules.length > 0) {
      setSelectedDay(day);
      setSelectedDaySchedules(daySchedules);
      setSelectedSchedule(null);
    }
  };

  const getSchedulesForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return schedules.filter(s => {
      if (s.scheduleType === 'selected_dates' && Array.isArray(s.selectedDates) && s.selectedDates.length) {
        return s.selectedDates.includes(dateStr);
      }
      const start = new Date(s.startDate);
      const end = new Date(s.endDate);
      const check = new Date(dateStr);
      return check >= start && check <= end;
    });
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getDate() === day && 
           today.getMonth() === currentDate.getMonth() && 
           today.getFullYear() === currentDate.getFullYear();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const localeMap = { ja: 'ja-JP', en: 'en-US', pt: 'pt-BR', id: 'id-ID', vi: 'vi-VN', es: 'es-ES', tl: 'tl-PH' };
    return date.toLocaleDateString(localeMap[language] || 'en-US', { month: 'short', day: 'numeric' });
  };

  // Generate calendar days
  const calendarDays = [];
  
  // Empty cells for days before the first day of month
  for (let i = 0; i < startingDay; i++) {
    calendarDays.push(null);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Get unique courses for legend
  const uniqueCourses = [...new Map(schedules.map(s => [s.courseId, s])).values()];

  return (
    <div className="w-full">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="icon" onClick={prevMonth}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h3 className="text-xl font-bold text-gray-900">
          {language === 'ja' 
            ? `${currentDate.getFullYear()}年 ${monthNames[currentDate.getMonth()]}`
            : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
          }
        </h3>
        <Button variant="outline" size="icon" onClick={nextMonth}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Week days header */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {weekDays.map((day, index) => (
            <div 
              key={day} 
              className={`py-2 text-center text-xs font-semibold ${
                index === 0 ? 'text-red-500' : index === 6 ? 'text-blue-500' : 'text-gray-700'
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            const daySchedules = day ? getSchedulesForDate(day) : [];
            const dayOfWeek = index % 7;
            const hasSchedules = daySchedules.length > 0;
            
            return (
              <div 
                key={index}
                onClick={() => day && hasSchedules && handleDayClick(day)}
                className={`min-h-[38px] md:min-h-[48px] border-b border-r border-gray-100 p-0.5 transition-colors ${
                  !day ? 'bg-gray-50' : hasSchedules ? 'bg-white hover:bg-green-50 cursor-pointer' : 'bg-white'
                } ${dayOfWeek === 6 ? 'border-r-0' : ''} ${selectedDay === day ? 'bg-green-50 ring-2 ring-[#00A651] ring-inset' : ''}`}
              >
                {day && (
                  <>
                    <div className={`text-xs font-medium ${
                      isToday(day) 
                        ? 'bg-[#00A651] text-white w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center mx-auto'
                        : dayOfWeek === 0 ? 'text-red-500 text-center' : dayOfWeek === 6 ? 'text-blue-500 text-center' : 'text-gray-700 text-center'
                    }`}>
                      {day}
                    </div>
                    {/* Desktop: Show schedule details */}
                    <div className="hidden md:block">
                      {daySchedules.slice(0, 1).map((schedule, idx) => {
                        const color = getColorFor(schedule.courseId);
                        const isFull = schedule.status === 'full' || (schedule.capacity > 0 && schedule.enrolled >= schedule.capacity);
                        const isClosed = schedule.status === 'closed';
                        const isInProgress = schedule.status === 'in_progress';
                        return (
                          <div
                            key={`${schedule.id}-${idx}`}
                            className={`w-full text-left px-0.5 rounded text-[10px] truncate ${isClosed ? 'bg-gray-100 text-gray-400 line-through' : isInProgress ? 'bg-blue-100 text-blue-700' : isFull ? 'bg-red-100 text-red-700' : `${color.light} ${color.text}`}`}
                          >
                            {isClosed ? t('scheduleCalendar.closed') + ' ' : isInProgress ? <span className="font-bold">{t('scheduleCalendar.inProgress')} </span> : isFull ? <span className="font-bold">{t('scheduleCalendar.full')} </span> : null}
                            {(() => { const n = getCourseName(schedule); return n.length > 6 ? n.slice(0, 6) + '...' : n; })()}
                          </div>
                        );
                      })}
                      {daySchedules.length > 1 && (
                        <div className="text-[10px] text-gray-500 text-center">
                          +{daySchedules.length - 1}
                        </div>
                      )}
                    </div>
                    {/* Mobile: Show colored dots */}
                    <div className="md:hidden flex flex-wrap justify-center gap-0.5">
                      {daySchedules.slice(0, 3).map((schedule, idx) => {
                        const color = getColorFor(schedule.courseId);
                        const isFull = schedule.status === 'full' || (schedule.capacity > 0 && schedule.enrolled >= schedule.capacity);
                        const isClosed = schedule.status === 'closed';
                        const isInProgress = schedule.status === 'in_progress';
                        return (
                          <div
                            key={`${schedule.id}-${idx}`}
                            className={`w-1.5 h-1.5 rounded-full ${isClosed ? 'bg-gray-400' : isInProgress ? 'bg-blue-500' : isFull ? 'bg-red-500' : color.bg}`}
                          />
                        );
                      })}
                      {daySchedules.length > 3 && (
                        <span className="text-[8px] text-gray-500">+{daySchedules.length - 3}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend - below calendar */}
      <div className="flex flex-wrap gap-3 mt-4 justify-center">
        {uniqueCourses.map(course => {
          const color = getColorFor(course.courseId);
          return (
            <div key={course.courseId} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${color.bg}`}></div>
              <span className="text-xs text-gray-600">{getCourseName(course)}</span>
            </div>
          );
        })}
      </div>

      {/* Selected Day Schedules List */}
      {selectedDaySchedules.length > 0 && !selectedSchedule && (
        <div className="mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-[#00A651] px-4 py-3 flex justify-between items-center">
            <h4 className="text-white font-bold">
              {language === 'ja' ? `${currentDate.getMonth() + 1}月${selectedDay}日${t('scheduleCalendar.coursesOnDay')}` : `${monthNames[currentDate.getMonth()]} ${selectedDay}${t('scheduleCalendar.coursesOnDay')}`}
            </h4>
            <button 
              onClick={() => { setSelectedDaySchedules([]); setSelectedDay(null); }}
              className="text-white hover:text-gray-200 text-lg"
            >
              ✕
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {selectedDaySchedules.map((schedule) => {
              const color = getColorFor(schedule.courseId);
              return (
                <button
                  key={schedule.id}
                  onClick={() => setSelectedSchedule(schedule)}
                  className="w-full p-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${color.bg}`}></div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{getCourseName(schedule)}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {schedule.location || t('scheduleCalendar.locationTBD')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      schedule.enrolled >= schedule.capacity 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {schedule.enrolled >= schedule.capacity 
                        ? t('scheduleCalendar.full')
                        : t('scheduleCalendar.seatsRemaining').replace('{n}', schedule.capacity - schedule.enrolled)
                      }
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Schedule Detail */}
      {selectedSchedule && (
        <div className="mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className={`${getColorFor(selectedSchedule.courseId).bg} px-4 py-3 flex justify-between items-center`}>
            <h4 className="text-white font-bold">{getCourseName(selectedSchedule)}</h4>
            <button 
              onClick={() => setSelectedSchedule(null)}
              className="text-white hover:text-gray-200"
            >
              ✕
            </button>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('scheduleCalendar.date')}</p>
                {selectedSchedule.scheduleType === 'selected_dates' && Array.isArray(selectedSchedule.selectedDates) && selectedSchedule.selectedDates.length ? (
                  <p className="font-medium">
                    {[...selectedSchedule.selectedDates].sort().map(d => formatDate(d)).join('、')}
                  </p>
                ) : (
                  <p className="font-medium">
                    {formatDate(selectedSchedule.startDate)}
                    {selectedSchedule.startDate !== selectedSchedule.endDate && (
                      <> ~ {formatDate(selectedSchedule.endDate)}</>
                    )}
                  </p>
                )}
              </div>
              {selectedSchedule.location && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('scheduleCalendar.location')}</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-[#00A651]" />
                    {selectedSchedule.location}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('scheduleCalendar.capacity')}</p>
                <p className="font-medium flex items-center gap-1">
                  <Users className="h-4 w-4 text-[#00A651]" />
                  {selectedSchedule.enrolled}/{selectedSchedule.capacity}
                  {t('scheduleCalendar.enrolled')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('scheduleCalendar.status')}</p>
                <span data-testid="schedule-detail-status" className={`px-2 py-1 rounded-full text-xs font-medium ${
                  selectedSchedule.status === 'closed'
                    ? 'bg-gray-100 text-gray-500'
                    : selectedSchedule.status === 'in_progress'
                      ? 'bg-blue-100 text-blue-700'
                      : selectedSchedule.status === 'full' || selectedSchedule.enrolled >= selectedSchedule.capacity 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                }`}>
                  {selectedSchedule.status === 'closed'
                    ? t('scheduleCalendar.closed')
                    : selectedSchedule.status === 'in_progress'
                      ? t('scheduleCalendar.inProgress')
                      : selectedSchedule.status === 'full' || selectedSchedule.enrolled >= selectedSchedule.capacity 
                        ? t('scheduleCalendar.full')
                        : t('scheduleCalendar.open')
                  }
                </span>
              </div>
            </div>
            {selectedSchedule.notes && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-600 font-medium mb-1">{t('scheduleCalendar.notes')}</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedSchedule.notes}</p>
              </div>
            )}
            <div className="mt-4">
              <Link to="/apply">
                <Button 
                  className="w-full bg-[#00A651] hover:bg-[#008a43] text-white"
                  disabled={selectedSchedule.status === 'closed' || selectedSchedule.status === 'in_progress' || selectedSchedule.status === 'full' || selectedSchedule.enrolled >= selectedSchedule.capacity}
                  data-testid="schedule-apply-button"
                >
                  {selectedSchedule.status === 'closed' ? t('scheduleCalendar.closed') : selectedSchedule.status === 'in_progress' ? t('scheduleCalendar.inProgress') : t('scheduleCalendar.applyForCourse')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleCalendar;
